import { usePlanOfCareStore } from "../../stores/planOfCareStore";
import { DateTime } from "luxon";
import { Card, CardContent } from "../../components/ui/card";
import { cn } from "../../lib/utils";
import { Skeleton } from "../../components/ui/skeleton";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { usePlanOfCareVisitAvailability, PlanOfCareAvailability, PlanOfCareAvailabilityDebugProvider, PlanOfCareAvailabilityDebugSlot } from "../../hooks/usePlanOfCareVisitAvailability";
import { CalendarCheckIcon, CalendarXIcon, ChevronLeftIcon, ChevronRightIcon, CircleAlertIcon, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { parseAsInteger, useQueryState } from "nuqs";
import { usePlanOfCareDetails } from "../../hooks/usePlanOfCareDetails";
import { convertE164ToReadable } from "../../utils/utils";
import { RootState } from "../../store";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { ChevronsUpDownIcon } from "lucide-react";

// Applies a filter to a list of candidate days while recording, for each day that
// gets dropped, why it was dropped (first reason wins - later steps don't override).
// Used to explain crossed-out days in the clinic-user debug view.
function applyDayFilter(currentDays: DateTime[], predicate: (day: DateTime) => boolean, reason: string, reasonMap: Map<string, string>): DateTime[] {
  const kept: DateTime[] = [];
  for (const day of currentDays) {
    if (predicate(day)) {
      kept.push(day);
    } else {
      const key = day.toISODate();
      if (key && !reasonMap.has(key)) reasonMap.set(key, reason);
    }
  }
  return kept;
}

// Same idea as applyDayFilter, but for steps that slice/replace the list wholesale
// (e.g. gap slicing) rather than filtering with a predicate per day.
function applyDaySlice(currentDays: DateTime[], nextDays: DateTime[], reason: string, reasonMap: Map<string, string>): DateTime[] {
  const keptKeys = new Set(nextDays.map((day) => day.toISODate()));
  for (const day of currentDays) {
    const key = day.toISODate();
    if (key && !keptKeys.has(key) && !reasonMap.has(key)) {
      reasonMap.set(key, reason);
    }
  }
  return nextDays;
}

function buildBlockedDatesSet(selectedDate: DateTime, gap: number, currentPeriod: any, selectedAppointmentId: string, bookingOutcomes: any[]): Set<string> {
  const blockedDates = new Set<string>();

  // Add selected date and its gap range
  for (let offset = -gap; offset <= gap; offset++) {
    const blockedDay = selectedDate.plus({ days: offset });
    const key = blockedDay.toISODate();
    if (key) blockedDates.add(key);
  }

  // Add all already scheduled appointments with their gap ranges
  for (const appointment of currentPeriod.appointments) {
    if (appointment.id === selectedAppointmentId) continue;

    let appointmentDate: DateTime | undefined = undefined;
    const outcome = bookingOutcomes.find((o) => o.id === appointment.id);

    if (outcome?.start) {
      appointmentDate = outcome.start;
    } else if (appointment.status !== "unscheduled" && appointment.start) {
      appointmentDate = DateTime.fromISO(appointment.start);
    }

    if (appointmentDate) {
      for (let offset = -gap; offset <= gap; offset++) {
        const blockedDay = appointmentDate.plus({ days: offset });
        const key = blockedDay.toISODate();
        if (key) blockedDates.add(key);
      }
    }
  }

  // Add extra appointments and their gaps
  if (currentPeriod.extraAppointments?.length > 0) {
    for (const appointment of currentPeriod.extraAppointments) {
      const outcome = bookingOutcomes.find((outcome) => outcome.id === appointment.id.toString());

      let extraDate: DateTime | undefined = undefined;
      if (outcome?.start) {
        extraDate = outcome.start;
      } else if (appointment.start) {
        extraDate = DateTime.fromISO(appointment.start);
      }

      if (extraDate) {
        for (let offset = -gap; offset <= gap; offset++) {
          const blockedDay = extraDate.plus({ days: offset });
          const key = blockedDay.toISODate();
          if (key) blockedDates.add(key);
        }
      }
    }
  }

  return blockedDates;
}

/**
 * Check whether a single day has at least one bookable provider slot.
 * Mirrors the logic used in the Days component (2-hour-from-now filter for today).
 */
function dayHasAvailability(day: DateTime, availabilityData: any, selectedProviderValue?: string): boolean {
  if (!availabilityData) return false;

  const dateKey = day.toFormat("yyyy-MM-dd");
  const isToday = day.hasSame(DateTime.now(), "day");
  const nowPlusTwoHours = DateTime.now().plus({ hours: 2 });

  const providerIds = !selectedProviderValue || selectedProviderValue === "all" ? Object.keys(availabilityData) : [selectedProviderValue];

  return providerIds.some((providerId) => {
    const daySlots = availabilityData[providerId]?.[dateKey];
    if (!daySlots || !Array.isArray(daySlots)) return false;

    return daySlots.some((slot: { startISO: string; endISO: string }) => {
      const start = DateTime.fromISO(slot.startISO);
      if (isToday && start.toMillis() < nowPlusTwoHours.toMillis()) return false;
      return true;
    });
  });
}

function getAvailableDays(days: DateTime[], blockedDates: Set<string>, minDate?: DateTime, maxDate?: DateTime, availabilityData?: any, selectedProviderValue?: string): DateTime[] {
  const today = DateTime.now().startOf("day");

  return days.filter((day) => {
    // Must be today or future
    if (day < today) return false;

    // Must be within date range if specified
    if (minDate && day < minDate) return false;
    if (maxDate && day > maxDate) return false;

    // Must not be blocked
    const key = day.toISODate();
    if (!key || blockedDates.has(key)) return false;

    // If availability data is provided, must have real provider slots
    if (availabilityData) {
      if (!dayHasAvailability(day, availabilityData, selectedProviderValue)) return false;
    }

    return true;
  });
}

/**
 * Greedy placement: try to place `count` appointments on `sortedDays`
 * (already sorted in the traversal direction) respecting the gap constraint.
 * `anchor` is the starting reference point (e.g. the selected date).
 */
function greedyPlace(count: number, sortedDays: DateTime[], anchor: DateTime, gap: number, direction: "forward" | "backward"): { placed: number; usedDays: Set<string> } {
  const usedDays = new Set<string>();
  let lastDay = anchor;
  let placed = 0;

  for (const day of sortedDays) {
    if (placed >= count) break;

    const minDistance = gap + 1;
    const distance = Math.abs(day.diff(lastDay, "days").days);

    if (distance >= minDistance) {
      const key = day.toISODate();
      if (key && !usedDays.has(key)) {
        usedDays.add(key);
        lastDay = day;
        placed++;
      }
    }
  }

  return { placed, usedDays };
}

/**
 * Compute a compliance warning for the currently selected date.
 *
 * Rules:
 * - Same-type appointments (relative to the one being booked) can be placed
 *   on either side of the selected date (flexible ordering).
 * - Different-type appointments must maintain their original plan-of-care
 *   order relative to the one being booked.
 * - Availability data is factored in so the warning reflects real bookable
 *   days, not just structural (gap / date-range) constraints.
 */
function computeComplianceWarning(
  selectedDate: DateTime,
  selectedAppointment: { id: string; appointmentTypeId: string },
  currentPeriod: any,
  gap: number,
  bookingOutcomes: any[],
  days: DateTime[],
  crossCaseStudyBlockedDates: Set<string>,
  availabilityData?: any,
  selectedProviderValue?: string,
): { blocked: boolean; direction: "next" | "previous" | "both" | null } {
  const allAppointments = currentPeriod.appointments;
  const currentIndex = allAppointments.findIndex((a: any) => a.id === selectedAppointment.id);

  // Gather remaining unscheduled appointments (not yet booked in this session)
  const remaining = allAppointments.filter((a: any) => {
    if (a.id === selectedAppointment.id) return false;
    if (a.status !== "unscheduled") return false;
    if (bookingOutcomes.some((o) => o.id === a.id)) return false;
    return true;
  });

  if (remaining.length === 0) return { blocked: false, direction: null };

  // Partition into must-before, must-after, and flexible (same type)
  const mustBeBefore: any[] = [];
  const mustBeAfter: any[] = [];
  const flexible: any[] = [];

  for (const a of remaining) {
    const idx = allAppointments.indexOf(a);
    if (a.appointmentTypeId === selectedAppointment.appointmentTypeId) {
      flexible.push(a);
    } else if (idx < currentIndex) {
      mustBeBefore.push(a);
    } else {
      mustBeAfter.push(a);
    }
  }

  // Build blocked dates (selected date + gap + all other scheduled appointments + gap)
  const blockedDates = buildBlockedDatesSet(selectedDate, gap, currentPeriod, selectedAppointment.id, bookingOutcomes);
  crossCaseStudyBlockedDates.forEach((dateKey) => blockedDates.add(dateKey));

  // Get all structurally + availability-available days
  const allAvailable = getAvailableDays(days, blockedDates, undefined, undefined, availabilityData, selectedProviderValue);

  // Split into before / after the selected date
  const daysBefore = allAvailable.filter((d) => d < selectedDate).sort((a, b) => a.toMillis() - b.toMillis());

  const daysAfter = allAvailable.filter((d) => d > selectedDate).sort((a, b) => a.toMillis() - b.toMillis());

  // --- Place must-before (earliest-first, forward from start of period toward selected date) ---
  const { placed: placedBefore, usedDays: usedBefore } = greedyPlace(mustBeBefore.length, daysBefore, daysBefore.length > 0 ? daysBefore[0].minus({ days: gap + 1 }) : selectedDate, gap, "forward");
  const canPlaceBefore = placedBefore === mustBeBefore.length;

  // --- Place must-after (earliest-first, forward from selected date) ---
  const { placed: placedAfter, usedDays: usedAfter } = greedyPlace(mustBeAfter.length, daysAfter, selectedDate, gap, "forward");
  const canPlaceAfter = placedAfter === mustBeAfter.length;

  // --- Place flexible in any remaining days (both sides) ---
  const remainingDays = allAvailable
    .filter((d) => {
      const key = d.toISODate();
      return key && !usedBefore.has(key) && !usedAfter.has(key);
    })
    .sort((a, b) => a.toMillis() - b.toMillis());

  const { placed: placedFlexible } = greedyPlace(flexible.length, remainingDays, selectedDate, gap, "forward");

  // If forward pass didn't place all flexible, try backward for the ones that remain
  let canPlaceFlexible = placedFlexible === flexible.length;
  if (!canPlaceFlexible) {
    // Try a combined approach: place flexible on both sides using all remaining days
    const remainingReversed = [...remainingDays].sort((a, b) => b.toMillis() - a.toMillis());
    const { placed: placedFlexBack } = greedyPlace(flexible.length, remainingReversed, selectedDate, gap, "backward");
    if (placedFlexBack === flexible.length) {
      canPlaceFlexible = true;
    } else {
      // Last resort: interleave both directions
      // Place as many forward as possible, then try backward for the rest
      const usedFlexDays = new Set<string>();
      let flexPlaced = 0;
      let lastForward = selectedDate;
      let lastBackward = selectedDate;

      // Alternate placing forward and backward
      const fwdDays = remainingDays.filter((d) => d > selectedDate);
      const bwdDays = [...remainingDays.filter((d) => d < selectedDate)].sort((a, b) => b.toMillis() - a.toMillis());

      let fi = 0,
        bi = 0;
      while (flexPlaced < flexible.length && (fi < fwdDays.length || bi < bwdDays.length)) {
        // Try forward
        while (fi < fwdDays.length && flexPlaced < flexible.length) {
          const d = fwdDays[fi];
          fi++;
          const dist = Math.abs(d.diff(lastForward, "days").days);
          const key = d.toISODate();
          if (dist >= gap + 1 && key && !usedFlexDays.has(key)) {
            usedFlexDays.add(key);
            lastForward = d;
            flexPlaced++;
            break;
          }
        }
        // Try backward
        while (bi < bwdDays.length && flexPlaced < flexible.length) {
          const d = bwdDays[bi];
          bi++;
          const dist = Math.abs(d.diff(lastBackward, "days").days);
          const key = d.toISODate();
          if (dist >= gap + 1 && key && !usedFlexDays.has(key)) {
            usedFlexDays.add(key);
            lastBackward = d;
            flexPlaced++;
            break;
          }
        }
      }
      canPlaceFlexible = flexPlaced === flexible.length;
    }
  }

  // If everything fits, no warning
  if (canPlaceBefore && canPlaceAfter && canPlaceFlexible) {
    return { blocked: false, direction: null };
  }

  // Determine which direction is problematic
  const hasAfterProblem = !canPlaceAfter;
  const hasBeforeProblem = !canPlaceBefore;
  // Flexible appointments that can't be placed indicate general lack of space
  const hasFlexProblem = !canPlaceFlexible;

  if ((hasAfterProblem || hasFlexProblem) && (hasBeforeProblem || hasFlexProblem) && hasAfterProblem !== hasBeforeProblem) {
    return { blocked: true, direction: "both" };
  }
  if (hasAfterProblem && hasBeforeProblem) {
    return { blocked: true, direction: "both" };
  }
  if (hasAfterProblem || (hasFlexProblem && mustBeAfter.length > 0)) {
    return { blocked: true, direction: "next" };
  }
  if (hasBeforeProblem || (hasFlexProblem && mustBeBefore.length > 0)) {
    return { blocked: true, direction: "previous" };
  }
  // Flexible-only problem: not enough room on either side
  if (hasFlexProblem) {
    return { blocked: true, direction: "both" };
  }

  return { blocked: false, direction: null };
}

export function ScheduleSection() {
  const {
    uniqueId,
    currentPeriod,
    selectedProvider,
    selectedAppointment,
    gap,
    date,
    setDate,
    bookableDays,
    setBookableDays,
    bookingOutcomes,
    authorization,
    authorizationsByType,
    scheduledVisitDates,
    ignoreAuthorization,
    caseStudyId,
    crossCaseStudyBlockedDates,
    planOfCareId,
    debugMode,
    setBookableDayReasons,
  } = usePlanOfCareStore();

  const clinicUser = useSelector((state: RootState) => state.auth.user);
  const debugActive = debugMode && Boolean(clinicUser?.token);

  const days = useMemo(() => {
    if (!currentPeriod) return [] as DateTime[];
    const start = DateTime.fromISO(currentPeriod.periodStart.slice(0, 10));
    return Array.from({ length: 7 }).map((_, idx) => start.plus({ days: idx }));
  }, [currentPeriod]);

  const isLoadingDays = useMemo(() => {
    return days.length === 0;
  }, [days]);

  const [bookableDaysReady, setBookableDaysReady] = useState(false);

  const unScheduledAppointments = useMemo(() => {
    if (!currentPeriod) return [];
    return currentPeriod.appointments.filter((appointment) => appointment.status === "unscheduled" || appointment.status === "unauthorized");
  }, [currentPeriod]);

  const noVisitsThisWeek = useMemo(() => {
    return !isLoadingDays && (unScheduledAppointments.length === 0 || unScheduledAppointments.every((appointment) => bookingOutcomes.some((outcome) => outcome.id === appointment.id)));
  }, [isLoadingDays, unScheduledAppointments, bookingOutcomes]);

  // Set the bookable days based on previously scheduled appointments
  useEffect(() => {
    // reset readiness while recomputing
    setBookableDaysReady(false);
    if (noVisitsThisWeek) return;
    if (!currentPeriod) return;
    if (!selectedAppointment.id) return;
    if (!days.length) return;
    if (gap === undefined || gap === null) return;

    let previousAppointment = undefined;
    let nextAppointment = undefined;
    let bookableDays = days;
    // Records, per excluded day, why it isn't bookable - surfaced as a tooltip
    // on crossed-out days when a clinic user has the debug view active.
    const reasonMap = new Map<string, string>();

    const todayIndex = days.findIndex((day) => day.hasSame(DateTime.now(), "day"));
    if (todayIndex !== -1) {
      bookableDays = applyDaySlice(bookableDays, days.slice(todayIndex), "This date has already passed.", reasonMap);
    }

    if (!ignoreAuthorization && authorization === undefined && authorizationsByType === undefined) {
      bookableDays = applyDaySlice(bookableDays, [], "No authorization is on file for this case study.", reasonMap);
    }

    if (!ignoreAuthorization && authorization !== undefined) {
      bookableDays = applyDayFilter(
        bookableDays,
        (day) => {
          const dayKey = day.toISODate();
          if (!dayKey) return true;
          if (dayKey >= authorization.startDate && dayKey <= authorization.endDate) {
            const existingCount = scheduledVisitDates.filter((d) => d >= authorization.startDate && d <= authorization.endDate).length;
            const sessionCount = bookingOutcomes.filter((outcome) => {
              if (!outcome.start || outcome.caseStudyId !== caseStudyId) return false;
              const outcomeKey = outcome.start.toISODate();
              return outcomeKey ? outcomeKey >= authorization.startDate && outcomeKey <= authorization.endDate : false;
            }).length;
            if (existingCount + sessionCount < authorization.visitLimit) return true;
          }
          return false;
        },
        "Outside the authorized visit window, or the authorized visit limit has already been reached.",
        reasonMap,
      );
    }

    if (!ignoreAuthorization && authorizationsByType !== undefined) {
      bookableDays = applyDayFilter(
        bookableDays,
        (day) => {
          const dayKey = day.toISODate();
          if (!dayKey) return true;
          return Object.values(authorizationsByType).every(
            (ranges) =>
              ranges.length === 0 ||
              ranges.some((range) => {
                if (dayKey < range.startDate || dayKey > range.endDate) return false;
                const existingCount = scheduledVisitDates.filter((d) => d >= range.startDate && d <= range.endDate).length;
                const sessionCount = bookingOutcomes.filter((outcome) => {
                  if (!outcome.start || outcome.caseStudyId !== caseStudyId) return false;
                  const outcomeKey = outcome.start.toISODate();
                  return outcomeKey ? outcomeKey >= range.startDate && outcomeKey <= range.endDate : false;
                }).length;
                return range.visitLimit === null || existingCount + sessionCount < range.visitLimit;
              }),
          );
        },
        "Outside the authorized visit window for one or more appointment types, or the visit limit has already been reached.",
        reasonMap,
      );
    }

    if (currentPeriod) {
      for (let i = 0; i < currentPeriod.appointments.length; i++) {
        if (currentPeriod.appointments[i].id === selectedAppointment.id) {
          if (i < currentPeriod.appointments.length - 1) {
            nextAppointment = currentPeriod.appointments[i + 1];
          }
          break;
        }
        previousAppointment = currentPeriod.appointments[i];
      }
    }

    if (previousAppointment) {
      const bookingOutcome = bookingOutcomes.find((outcome) => outcome.id === previousAppointment.id);
      const reason = "Too close to a previous appointment in this plan of care (minimum gap not met).";
      if (previousAppointment.status !== "unscheduled" && previousAppointment.start) {
        const previousAppointmentDate = DateTime.fromISO(previousAppointment.start);
        const dayIndex = bookableDays.findIndex((day) => day.hasSame(previousAppointmentDate, "day"));
        if (dayIndex !== -1) {
          bookableDays = applyDaySlice(bookableDays, bookableDays.slice(dayIndex + 1 + gap), reason, reasonMap);
        }
      } else if (bookingOutcome?.start) {
        const previousAppointmentDate = bookingOutcome.start;
        const dayIndex = bookableDays.findIndex((day) => day.hasSame(previousAppointmentDate, "day"));
        if (dayIndex !== -1) {
          bookableDays = applyDaySlice(bookableDays, bookableDays.slice(dayIndex + 1 + gap), reason, reasonMap);
        }
      }
    }

    if (nextAppointment) {
      const bookingOutcome = bookingOutcomes.find((outcome) => outcome.id === nextAppointment.id);
      const reason = "Too close to an upcoming appointment in this plan of care (minimum gap not met).";
      if (nextAppointment.status !== "unscheduled" && nextAppointment.start) {
        const nextAppointmentDate = DateTime.fromISO(nextAppointment.start);
        const dayIndex = bookableDays.findIndex((day) => day.hasSame(nextAppointmentDate, "day"));
        if (dayIndex !== -1) {
          const endIndex = Math.max(0, dayIndex - gap);
          bookableDays = applyDaySlice(bookableDays, bookableDays.slice(0, endIndex), reason, reasonMap);
        }
      } else if (bookingOutcome?.start) {
        const nextAppointmentDate = bookingOutcome.start;
        const dayIndex = bookableDays.findIndex((day) => day.hasSame(nextAppointmentDate, "day"));
        if (dayIndex !== -1) {
          const endIndex = Math.max(0, dayIndex - gap);
          bookableDays = applyDaySlice(bookableDays, bookableDays.slice(0, endIndex), reason, reasonMap);
        }
      }
    }

    // Remove days impacted by extra appointments, honoring gap on both sides,
    // and applied on the remaining bookable days (after prev/next constraints)
    if (currentPeriod?.extraAppointments && currentPeriod.extraAppointments.length > 0) {
      const blockedDateKeys = new Set<string>();

      for (const appointment of currentPeriod.extraAppointments) {
        const outcome = bookingOutcomes.find((outcome) => outcome.id === appointment.id.toString());

        let extraDate: DateTime | undefined = undefined;
        if (outcome?.start) {
          extraDate = outcome.start;
        } else if (appointment.start) {
          extraDate = DateTime.fromISO(appointment.start);
        }

        if (!extraDate) continue;

        for (let offset = -gap; offset <= gap; offset++) {
          const candidate = extraDate.plus({ days: offset });
          const key = candidate.toISODate();
          if (key) blockedDateKeys.add(key);
        }
      }

      bookableDays = applyDayFilter(
        bookableDays,
        (day) => {
          const key = day.toISODate();
          return key ? !blockedDateKeys.has(key) : true;
        },
        "Too close to another appointment on this plan of care (minimum gap not met).",
        reasonMap,
      );
    }

    // Block exact dates of ALL scheduled/booked appointments in this period
    // (the prev/next gap slicing above only handles adjacent appointments)
    const samePeriodBlockedDates = new Set<string>();
    for (const appointment of currentPeriod.appointments) {
      if (appointment.id === selectedAppointment.id) continue;
      const outcome = bookingOutcomes.find((o) => o.id === appointment.id);
      let apptDate: DateTime | undefined;
      if (outcome?.start) {
        apptDate = outcome.start;
      } else if (appointment.status !== "unscheduled" && appointment.start) {
        apptDate = DateTime.fromISO(appointment.start);
      }
      if (apptDate) {
        const key = apptDate.toISODate();
        if (key) samePeriodBlockedDates.add(key);
      }
    }
    if (samePeriodBlockedDates.size > 0) {
      bookableDays = applyDayFilter(
        bookableDays,
        (day) => {
          const key = day.toISODate();
          return key ? !samePeriodBlockedDates.has(key) : true;
        },
        "Another appointment is already scheduled on this date for this case study.",
        reasonMap,
      );
    }

    if (crossCaseStudyBlockedDates.size > 0) {
      bookableDays = applyDayFilter(
        bookableDays,
        (day) => {
          const key = day.toISODate();
          return key ? !crossCaseStudyBlockedDates.has(key) : true;
        },
        "Another case study already has an appointment scheduled on this date.",
        reasonMap,
      );
    }

    setBookableDays(bookableDays);
    setBookableDayReasons(reasonMap);
    // mark as ready using the locally computed value
    setBookableDaysReady(bookableDays.length > 0);
  }, [
    days,
    currentPeriod,
    selectedAppointment,
    gap,
    noVisitsThisWeek,
    authorization,
    authorizationsByType,
    scheduledVisitDates,
    ignoreAuthorization,
    bookingOutcomes,
    caseStudyId,
    crossCaseStudyBlockedDates,
  ]);

  const { data, isLoading } = usePlanOfCareVisitAvailability({
    uniqueId: uniqueId,
    enabled: Boolean(bookableDaysReady && uniqueId && currentPeriod && selectedAppointment.appointmentTypeId && !noVisitsThisWeek),
    startDate: currentPeriod?.periodStart.slice(0, 10) || "",
    endDate: currentPeriod?.periodEnd.slice(0, 10) || "",
    appointmentTypeId: selectedAppointment.appointmentTypeId,
    planOfCareId: planOfCareId,
    debug: debugActive,
    token: clinicUser?.token,
  });

  const providerOptions = useMemo(() => {
    if (data && data.availableProviders && data.availableProviders.length > 0) {
      return data.availableProviders.map((provider: { providerName: string; providerId: string }) => ({
        label: provider.providerName,
        value: provider.providerId,
      }));
    }
    return [];
  }, [data]);

  const timeSlots = useMemo(() => {
    const slots: {
      [key: string]: {
        [key: string]: {
          [key: string]: { startISO: string; endISO: string };
        };
      };
    } = data?.availability;

    if (!slots) return {};
    return slots;
  }, [data, date]);

  // Helper function to get provider slots for a specific date
  const getProviderSlotsForDate = useCallback(
    (dayDate: DateTime) => {
      if (!timeSlots) return [];

      const dateKey = dayDate.toFormat("yyyy-MM-dd");
      const isTodaySelected = dayDate.hasSame(DateTime.now(), "day");
      const nowPlusTwoHours = DateTime.now().plus({ hours: 2 });

      const providerIds = selectedProvider.value === "all" ? Object.keys(timeSlots) : [selectedProvider.value];

      const grouped: {
        [timeLabel: string]: {
          start: DateTime;
          providers: { providerId: string; label: string; end: DateTime }[];
        };
      } = {};

      providerIds.forEach((providerId) => {
        const daySlots = (timeSlots as any)[providerId]?.[dateKey];
        if (!daySlots || !Array.isArray(daySlots)) return;

        daySlots.forEach((slot: { startISO: string; endISO: string }) => {
          const start = DateTime.fromISO(slot.startISO);
          const end = DateTime.fromISO(slot.endISO);

          // If booking for today, only show slots at least 2 hours from now
          if (isTodaySelected && start.toMillis() < nowPlusTwoHours.toMillis()) {
            return;
          }
          const timeLabel = start.toFormat("h:mm a");
          const label = providerOptions.find((option: { label: string; value: string }) => option.value === providerId)?.label || providerId;

          if (!grouped[timeLabel]) {
            grouped[timeLabel] = { start, providers: [] };
          }
          grouped[timeLabel].providers.push({ providerId, label, end });
        });
      });

      return Object.entries(grouped)
        .sort((a, b) => a[1].start.toMillis() - b[1].start.toMillis())
        .map(([label, { start, providers }]) => ({ label, start, providers }));
    },
    [timeSlots, selectedProvider, providerOptions],
  );

  const filteredProviderSlots = useMemo(() => {
    if (!date) return [];
    return getProviderSlotsForDate(date);
  }, [date, getProviderSlotsForDate]);

  // Automatically set date to first bookable day with availability, or next bookable day if none have availability
  useEffect(() => {
    if (!bookableDaysReady) return;
    if (bookableDays.length === 0) return;
    if (!timeSlots) return;

    const isDateValidForCurrentWeek = date && bookableDays.some((day) => day.hasSame(date, "day"));
    if (isDateValidForCurrentWeek) return;

    // Find first bookable day with availability
    const firstDayWithAvailability = bookableDays.find((day) => getProviderSlotsForDate(day).length > 0);

    if (firstDayWithAvailability) {
      setDate(firstDayWithAvailability);
    } else {
      // If no days have availability, just go to the first bookable day
      setDate(bookableDays[0]);
    }
  }, [bookableDaysReady, bookableDays, timeSlots, date, setDate, getProviderSlotsForDate]);

  const isAuthorizationBlocked = useMemo(() => {
    if (ignoreAuthorization || days.length === 0) return false;
    if (authorization === undefined && authorizationsByType === undefined) return true;
    return !days.some((day) => {
      const dayKey = day.toISODate();
      if (!dayKey) return false;

      if (authorization !== undefined) {
        if (dayKey < authorization.startDate || dayKey > authorization.endDate) return false;
        const used =
          scheduledVisitDates.filter((d) => d >= authorization.startDate && d <= authorization.endDate).length +
          bookingOutcomes.filter((o) => {
            if (!o.start || o.caseStudyId !== caseStudyId) return false;
            const k = o.start.toISODate();
            return k ? k >= authorization.startDate && k <= authorization.endDate : false;
          }).length;
        if (used >= authorization.visitLimit) return false;
      }

      if (authorizationsByType !== undefined) {
        const allTypesPass = Object.values(authorizationsByType).every((ranges) =>
          ranges.some((range) => {
            if (dayKey < range.startDate || dayKey > range.endDate) return false;
            const used =
              scheduledVisitDates.filter((d) => d >= range.startDate && d <= range.endDate).length +
              bookingOutcomes.filter((o) => {
                if (!o.start || o.caseStudyId !== caseStudyId) return false;
                const k = o.start.toISODate();
                return k ? k >= range.startDate && k <= range.endDate : false;
              }).length;
            return range.visitLimit === null || used < range.visitLimit;
          }),
        );
        if (!allTypesPass) return false;
      }

      return true;
    });
  }, [days, authorization, authorizationsByType, scheduledVisitDates, ignoreAuthorization, bookingOutcomes, caseStudyId]);

  const complianceWarning = useMemo(() => {
    if (!date || gap === undefined || gap === null || !currentPeriod || !selectedAppointment.id) {
      return { blocked: false, direction: null };
    }

    return computeComplianceWarning(date, selectedAppointment, currentPeriod, gap, bookingOutcomes, days, crossCaseStudyBlockedDates, data?.availability, selectedProvider.value);
  }, [date, gap, currentPeriod, selectedAppointment, bookingOutcomes, days, crossCaseStudyBlockedDates, data?.availability, selectedProvider]);

  return (
    <div className="border-t xl:border-l xl:border-t-0 border-border w-full xl:w-[420px]">
      <div className="p-6">
        <div className="flex items-center justify-between gap-6">
          <p className="text-base font-semibold">Schedule</p>
          <ProviderSelect providerOptions={providerOptions} />
        </div>

        <Days
          isLoadingDays={isLoadingDays}
          days={days}
          noVisitsThisWeek={noVisitsThisWeek}
          isLoading={isLoading}
          bookableDays={bookableDays}
          availability={data?.availability}
          providerWorkingDays={data?.providerWorkingDays}
          debugActive={debugActive}
        />

        <Slots
          noVisitsThisWeek={noVisitsThisWeek}
          isLoading={isLoadingDays}
          isLoadingAvailability={isLoading}
          filteredProviderSlots={filteredProviderSlots}
          complianceWarning={complianceWarning}
          isAuthorizationBlocked={isAuthorizationBlocked}
        />

        {debugActive && <AvailabilityDebugPanel isLoading={isLoading} data={data} date={date} selectedProviderValue={selectedProvider.value} />}
      </div>
    </div>
  );
}

const TIMELINE_REASON_COLORS = [
  "bg-amber-500/70 border-amber-600/70",
  "bg-rose-500/70 border-rose-600/70",
  "bg-sky-500/70 border-sky-600/70",
  "bg-violet-500/70 border-violet-600/70",
  "bg-orange-500/70 border-orange-600/70",
  "bg-pink-500/70 border-pink-600/70",
  "bg-cyan-500/70 border-cyan-600/70",
];

function useReasonColors(reasons: string[]): Map<string, string> {
  const key = reasons.join("|");
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const reason of reasons) {
      if (!map.has(reason)) {
        map.set(reason, TIMELINE_REASON_COLORS[map.size % TIMELINE_REASON_COLORS.length]);
      }
    }
    return map;
  }, [key]);
}

type TimelineSegment = {
  key: string;
  startMin: number;
  endMin: number;
  status: "open" | "excluded";
  reason?: string;
  tooltip: string;
};

function minutesOfDay(iso: string): number {
  const dt = DateTime.fromISO(iso);
  return dt.hour * 60 + dt.minute;
}

function formatHourTick(minutesFromMidnight: number): string {
  return DateTime.fromObject({ hour: Math.floor(minutesFromMidnight / 60) % 24, minute: minutesFromMidnight % 60 }).toFormat("h a");
}

// Shows, per provider, where the day's bookable slots are alongside the blocks
// the backend excluded (and why) — so exclusions read in context instead of a flat list.
function ProviderDayTimeline({
  date,
  availability,
  excludedSlots,
  excludedProviders,
  selectedProviderValue,
}: {
  date: DateTime | undefined;
  availability?: PlanOfCareAvailability["availability"];
  excludedSlots: PlanOfCareAvailabilityDebugSlot[];
  excludedProviders: PlanOfCareAvailabilityDebugProvider[];
  selectedProviderValue: string;
}) {
  const dayKey = date?.toFormat("yyyy-MM-dd");

  const rows = useMemo(() => {
    if (!dayKey) return [];
    const byProvider = new Map<string, { providerName: string; segments: TimelineSegment[] }>();

    if (availability) {
      for (const providerId of Object.keys(availability)) {
        const daySlots = availability[providerId]?.[dayKey];
        if (!daySlots?.length) continue;
        const entry = byProvider.get(providerId) ?? { providerName: providerId, segments: [] };
        for (const slot of daySlots) {
          entry.segments.push({
            key: `${providerId}-${slot.startISO}-open`,
            startMin: minutesOfDay(slot.startISO),
            endMin: minutesOfDay(slot.endISO),
            status: "open",
            tooltip: `Open ${DateTime.fromISO(slot.startISO).toFormat("h:mm a")}–${DateTime.fromISO(slot.endISO).toFormat("h:mm a")}`,
          });
        }
        byProvider.set(providerId, entry);
      }
    }

    for (const slot of excludedSlots) {
      if (DateTime.fromISO(slot.startISO).toFormat("yyyy-MM-dd") !== dayKey) continue;
      const entry = byProvider.get(slot.providerId) ?? { providerName: slot.providerName, segments: [] };
      entry.providerName = slot.providerName;
      entry.segments.push({
        key: `${slot.providerId}-${slot.startISO}-excluded`,
        startMin: minutesOfDay(slot.startISO),
        endMin: minutesOfDay(slot.endISO),
        status: "excluded",
        reason: slot.reason,
        tooltip: `${DateTime.fromISO(slot.startISO).toFormat("h:mm a")}–${DateTime.fromISO(slot.endISO).toFormat("h:mm a")} — ${slot.reason}`,
      });
      byProvider.set(slot.providerId, entry);
    }

    let entries = Array.from(byProvider.entries());
    if (selectedProviderValue !== "all") {
      entries = entries.filter(([providerId]) => providerId === selectedProviderValue);
    }

    return entries
      .map(([providerId, { providerName, segments }]) => ({
        providerId,
        providerName,
        segments: [...segments].sort((a, b) => a.startMin - b.startMin),
      }))
      .sort((a, b) => a.providerName.localeCompare(b.providerName));
  }, [dayKey, availability, excludedSlots, selectedProviderValue]);

  // Providers excluded from the whole list (not tied to any specific slot) —
  // shown as a full-width block on every day since there's no time range for them.
  const excludedProviderRows = useMemo(
    () => (selectedProviderValue === "all" ? excludedProviders : excludedProviders.filter((p) => p.providerId === selectedProviderValue)),
    [excludedProviders, selectedProviderValue],
  );

  const { rangeStart, rangeEnd } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const row of rows) {
      for (const seg of row.segments) {
        min = Math.min(min, seg.startMin);
        max = Math.max(max, seg.endMin);
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      return { rangeStart: 8 * 60, rangeEnd: 18 * 60 };
    }
    return { rangeStart: Math.floor(min / 60) * 60, rangeEnd: Math.ceil(max / 60) * 60 };
  }, [rows]);

  const totalMinutes = Math.max(rangeEnd - rangeStart, 60);

  const ticks = useMemo(() => {
    const result: number[] = [];
    for (let t = rangeStart; t <= rangeEnd; t += 120) result.push(t);
    return result;
  }, [rangeStart, rangeEnd]);

  const reasonColors = useReasonColors(rows.flatMap((r) => r.segments.filter((s) => s.status === "excluded").map((s) => s.reason || "")));

  if (!date) {
    return <p className="text-xs text-muted-foreground">Select a day to see its timeline.</p>;
  }

  if (rows.length === 0 && excludedProviderRows.length === 0) {
    return <p className="text-xs text-muted-foreground">No slot-level debug data for this day.</p>;
  }

  return (
    <div>
      <div className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1.5 items-center">
        <div />
        <div className="relative h-4 text-[10px] text-muted-foreground">
          {ticks.map((t) => (
            <span key={t} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((t - rangeStart) / totalMinutes) * 100}%` }}>
              {formatHourTick(t)}
            </span>
          ))}
        </div>

        {excludedProviderRows.map((provider) => (
          <Fragment key={provider.providerId}>
            <div className="text-xs font-medium truncate" title={provider.providerName}>
              {provider.providerName}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative h-6 rounded-none bg-slate-400/40 border border-slate-500/50 cursor-default" />
              </TooltipTrigger>
              <TooltipContent>Not shown: {provider.reason}</TooltipContent>
            </Tooltip>
          </Fragment>
        ))}

        {rows.map((row) => (
          <Fragment key={row.providerId}>
            <div className="text-xs font-medium truncate" title={row.providerName}>
              {row.providerName}
            </div>
            <div className="relative h-6 rounded-none bg-muted overflow-hidden">
              {row.segments.map((seg) => {
                const left = ((seg.startMin - rangeStart) / totalMinutes) * 100;
                const width = ((seg.endMin - seg.startMin) / totalMinutes) * 100;
                const colorClass = seg.status === "open" ? "bg-emerald-500/70 border-emerald-600/70" : reasonColors.get(seg.reason || "") || "bg-muted-foreground/40 border-border";
                return (
                  <Tooltip key={seg.key}>
                    <TooltipTrigger asChild>
                      {/* min-width is a px floor, not %, so short blocks stay a legible bar instead of collapsing into a sliver */}
                      <div className={cn("absolute top-0 h-full rounded-none border-r cursor-default", colorClass)} style={{ left: `${left}%`, width: `${width}%`, minWidth: "6px" }} />
                    </TooltipTrigger>
                    <TooltipContent>{seg.tooltip}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </Fragment>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-emerald-500/70 inline-block" /> Open
        </span>
        {Array.from(reasonColors.entries()).map(([reason, colorClass]) => (
          <span key={reason} className="inline-flex items-center gap-1">
            <span className={cn("size-2.5 rounded-sm inline-block", colorClass.split(" ")[0])} />
            {reason}
          </span>
        ))}
        {excludedProviderRows.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="size-2.5 rounded-sm bg-slate-400/40 border border-slate-500/50 inline-block" /> Not in allowed provider list
          </span>
        )}
      </div>
    </div>
  );
}

// Lets clinic users inspect why providers/slots were filtered out of the
// availability response, using the extra `debug=true` payload from the backend.
function AvailabilityDebugPanel({
  isLoading,
  data,
  date,
  selectedProviderValue,
}: {
  isLoading: boolean;
  data?: PlanOfCareAvailability;
  date: DateTime | undefined;
  selectedProviderValue: string;
}) {
  const debug = data?.debug;
  const excludedProviders = useMemo(() => debug?.providers ?? [], [debug]);
  const excludedSlots = useMemo(() => debug?.slots ?? [], [debug]);

  return (
    <Collapsible className="mt-4 rounded-md border border-dashed border-border bg-muted/40" defaultOpen>
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
        <span>{isLoading ? "Debug: loading..." : `Debug: ${excludedProviders.length} providers, ${excludedSlots.length} slots excluded`}</span>
        <ChevronsUpDownIcon className="size-3.5" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 space-y-3">
        {!debug && !isLoading && <p className="text-xs text-muted-foreground">No debug info in this response.</p>}

        {debug && (
          <div>
            <p className="text-xs font-semibold text-foreground mb-1.5">{date ? `Provider timeline — ${date.toFormat("ccc, MMM d")}` : "Provider timeline"}</p>
            <ProviderDayTimeline
              date={date}
              availability={data?.availability}
              excludedSlots={excludedSlots}
              excludedProviders={excludedProviders}
              selectedProviderValue={selectedProviderValue}
            />
          </div>
        )}

        {(excludedProviders.length > 0 || excludedSlots.length > 0) && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Raw JSON</summary>
            <pre className="max-h-72 overflow-auto rounded-md bg-background p-2 mt-1 text-[11px] leading-snug text-muted-foreground whitespace-pre-wrap">
              {JSON.stringify(data ?? null, null, 2)}
            </pre>
          </details>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function Days({
  isLoadingDays,
  days,
  noVisitsThisWeek,
  isLoading,
  bookableDays,
  availability,
  providerWorkingDays,
  debugActive,
}: {
  isLoadingDays: boolean;
  days: DateTime[];
  noVisitsThisWeek: boolean;
  isLoading: boolean;
  bookableDays: DateTime[];
  availability?: any;
  providerWorkingDays?: Record<string, string[]>;
  debugActive?: boolean;
}) {
  const { selectedProvider, bookableDayReasons } = usePlanOfCareStore();

  if (noVisitsThisWeek) {
    return null;
  }

  if (!noVisitsThisWeek && bookableDays.length === 0) {
    return null;
  }

  // Returns true when the provider(s) don't work on the given day at all,
  // as opposed to working that day but having no open slots.
  const isNonWorkingDay = (dayDate: DateTime): boolean => {
    if (!providerWorkingDays) return false;

    const dayName = dayDate.weekdayLong?.toLowerCase(); // "monday", "tuesday", etc.
    if (!dayName) return false;

    const providerIds = selectedProvider.value === "all" ? Object.keys(providerWorkingDays) : [selectedProvider.value];

    if (providerIds.length === 0) return false;

    // Non-working only when every relevant provider has no hours that day
    return providerIds.every((providerId) => {
      const workingDays = providerWorkingDays[providerId];
      if (!workingDays || workingDays.length === 0) return false;
      return !workingDays.includes(dayName);
    });
  };

  // Helper function to check if a day has availability (with same filters as filteredProviderSlots)
  const hasAvailability = (dayDate: DateTime) => {
    if (!availability) return false;
    const dateKey = dayDate.toFormat("yyyy-MM-dd");

    const isTodaySelected = dayDate.hasSame(DateTime.now(), "day");
    const nowPlusTwoHours = DateTime.now().plus({ hours: 2 });

    const providerIds = selectedProvider.value === "all" ? Object.keys(availability) : [selectedProvider.value];

    // Check if any provider has valid slots for this day
    return providerIds.some((providerId) => {
      const daySlots = availability[providerId]?.[dateKey];
      if (!daySlots || !Array.isArray(daySlots)) return false;

      // Check if there are any slots that pass the time filter
      return daySlots.some((slot: { startISO: string; endISO: string }) => {
        const start = DateTime.fromISO(slot.startISO);

        // If booking for today, only count slots at least 2 hours from now
        if (isTodaySelected && start.toMillis() < nowPlusTwoHours.toMillis()) {
          return false;
        }

        return true;
      });
    });
  };

  return (
    <div className="mt-3 w-full">
      <div className="w-full max-w-[420px]">
        <div className="grid grid-cols-7 gap-1">
          {isLoadingDays
            ? ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((label) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <div className="text-center text-muted-foreground text-xs font-medium tracking-wider">{label}</div>
                  <DayButton dateISO="" />
                </div>
              ))
            : days.map((dayDate) => {
                const isBookable = bookableDays.includes(dayDate);
                const isNonWorking = isNonWorkingDay(dayDate);
                const isDisabled = noVisitsThisWeek || !isBookable || isNonWorking;
                const hasSlots = hasAvailability(dayDate);
                const disabledReason = isNonWorking ? "The selected provider(s) don't work this day." : !isBookable ? bookableDayReasons.get(dayDate.toISODate() || "") : undefined;

                return (
                  <div key={dayDate.toISODate() || dayDate.toISO() || dayDate.toString()} className="flex flex-col items-center gap-1">
                    <div className="text-center text-muted-foreground text-xs font-medium tracking-wider">{dayDate.toFormat("ccc").toUpperCase()}</div>
                    <DayButton
                      disabled={isDisabled}
                      dateISO={dayDate.toISO() || ""}
                      hasAvailability={hasSlots}
                      isLoadingAvailability={isLoading}
                      isBookable={isBookable}
                      isNonWorkingDay={isNonWorking}
                      debugActive={debugActive}
                      disabledReason={disabledReason}
                    />
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}

function Slots({
  noVisitsThisWeek,
  isLoading,
  isLoadingAvailability,
  filteredProviderSlots,
  complianceWarning,
  isAuthorizationBlocked,
}: {
  noVisitsThisWeek: boolean;
  isLoading: boolean;
  isLoadingAvailability: boolean;
  filteredProviderSlots: {
    label: string;
    start: DateTime;
    providers: { providerId: string; label: string; end: DateTime }[];
  }[];
  complianceWarning: { blocked: boolean; direction: "next" | "previous" | "both" | null };
  isAuthorizationBlocked: boolean;
}) {
  const { uniqueId, selectedAppointment, appendBookingOutcomes, totalWeeks, bookableDays, setDate, setBookableDays, caseStudyId } = usePlanOfCareStore();

  const { data: detailsData } = usePlanOfCareDetails({ uniqueId, enabled: Boolean(uniqueId) });
  const clinicPhone = detailsData?.organization.phoneNumber ? convertE164ToReadable(detailsData.organization.phoneNumber) : null;

  const [week, setWeek] = useQueryState("week", parseAsInteger);

  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  const handleWaitlist = useCallback(async () => {
    setWaitlistLoading(true);

    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/waitlist`, {
        uniqueId: uniqueId,
        appointmentTypeId: selectedAppointment.appointmentTypeId,
        dates: bookableDays.map((day) => day.toISO()),
      });

      // Only append outcomes on success
      appendBookingOutcomes({
        status: "Joined Waitlist",
        id: selectedAppointment.id,
        appointmentTypeId: selectedAppointment.appointmentTypeId,
        appointmentTypeName: selectedAppointment.appointmentTypeName,
        appointmentDuration: selectedAppointment.appointmentDuration,
        caseStudyId: caseStudyId,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "There was an error joining the waitlist. Please try again or contact the clinic for help.");
    } finally {
      setWaitlistLoading(false);
    }
  }, [selectedAppointment, appendBookingOutcomes, uniqueId, bookableDays, caseStudyId]);

  if (isLoading) {
    return (
      <div className=" mt-4 grid grid-cols-1 gap-1.5 w-full xl:max-h-[276px] overflow-y-auto">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="w-full h-9 rounded-md" />
        ))}
      </div>
    );
  }

  if (!noVisitsThisWeek && bookableDays.length === 0) {
    return (
      <div className="mt-6 w-full max-w-[420px] md:max-w-none xl:max-w-[420px]">
        <Card className="bg-accent/50">
          <CardContent className="py-2">
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              <div className="flex items-center justify-center size-12 rounded-full bg-accent">
                <CircleAlertIcon className="size-6 stroke-[1.75] text-destructive" aria-hidden="true" />
              </div>
              <div className="max-w-xs">
                <p className="text-base font-semibold">{isAuthorizationBlocked ? "Not authorized this week" : "Can't book this week"}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {isAuthorizationBlocked ? (
                    <>
                      This week is outside of your insurance authorization or prescription.
                      <br />
                      {clinicPhone
                        ? `Please contact the clinic at ${clinicPhone} if you believe this is an error.`
                        : "Please contact the clinic if you believe this is an error."}
                    </>
                  ) : (
                    "No more available days this week. Select another week to continue booking."
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  disabled={week === 1}
                  onClick={() => {
                    setWeek(Math.max(1, (week || 1) - 1));
                    setDate(undefined);
                    setBookableDays([]);
                  }}
                  aria-label="Go to previous week"
                >
                  <ChevronLeftIcon className="mr-1" /> Previous week
                </Button>
                <Button
                  size="sm"
                  disabled={week === totalWeeks}
                  onClick={() => {
                    setWeek(Math.min(totalWeeks, (week || 1) + 1));
                    setDate(undefined);
                    setBookableDays([]);
                  }}
                  aria-label="Go to next week"
                >
                  Next week <ChevronRightIcon className="ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingAvailability) {
    return (
      <div className="mt-4 h-full py-16 px-3 flex flex-col gap-2 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading live availability...</p>
      </div>
    );
  }

  if (!noVisitsThisWeek && filteredProviderSlots.length === 0) {
    return (
      <div className="py-2 px-3">
        <div className="min-w-[290px] w-full">
          <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-8 xl:py-12">
            <>
              <div className="flex items-center justify-center size-10 rounded-full bg-accent">
                <CalendarXIcon className="size-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-sm text-center">No availability for this date</p>
            </>
            <Button onClick={handleWaitlist} disabled={waitlistLoading}>
              {waitlistLoading && <Loader2 className="size-4 animate-spin" />}
              Join waitlist for this week
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!noVisitsThisWeek && filteredProviderSlots.length > 0) {
    return (
      <div className="mt-3 xl:max-h-[276px] overflow-y-auto px-2">
        {complianceWarning.blocked && (
          <Alert variant="warning">
            <AlertTitle>
              {complianceWarning.direction === "both"
                ? "Other appointments will be blocked"
                : complianceWarning.direction === "next"
                  ? "Next appointment will be blocked"
                  : "Previous appointment will be blocked"}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {complianceWarning.direction === "both"
                ? "Booking this day will prevent you from scheduling other visits this week."
                : complianceWarning.direction === "next"
                  ? "Booking this day will prevent you from scheduling your next visit this week."
                  : "Booking this day will prevent you from scheduling your previous visit this week."}
            </AlertDescription>
          </Alert>
        )}
        <span className="text-muted-foreground text-[13px] font-medium">
          Need a different time?{" "}
          <Button variant="link" className="px-0 h-auto text-[13px] gap-1 items-center" onClick={handleWaitlist} disabled={waitlistLoading}>
            Join the waitlist
            {waitlistLoading && <Loader2 className="size-3 animate-spin" />}
          </Button>
        </span>
        {filteredProviderSlots.map(({ label, start, providers }: any) => (
          <Card key={label} className="p-0 border-none shadow-none bg-transparent">
            <CardContent className="px-0 py-1 space-y-2 ">
              <h3 className="text-sm font-medium">{label}</h3>
              <div className="flex flex-col gap-1">
                {providers.map(({ providerId, label: providerLabel, end }: any) => (
                  <SlotButton
                    key={`${start.toISO()}-${providerId}`}
                    providerId={providerId}
                    providerName={providerLabel}
                    start={start}
                    end={end}
                    isBookingLoading={isBookingLoading}
                    setIsBookingLoading={setIsBookingLoading}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-6 w-full max-w-[420px] md:max-w-none xl:max-w-[420px]">
      <Card className="bg-accent/50">
        <CardContent className="py-6">
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <div className="flex items-center justify-center size-12 rounded-full bg-accent">
              <CalendarCheckIcon className="size-6 stroke-[1.75] text-emerald-500" aria-hidden="true" />
            </div>
            <div className="max-w-xs">
              <p className="text-base font-semibold">All set for this week!</p>
              <p className="text-sm text-muted-foreground mt-1">Select another week to continue booking.</p>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                disabled={week === 1}
                onClick={() => {
                  setWeek(Math.max(1, (week || 1) - 1));
                  setDate(undefined);
                  setBookableDays([]);
                }}
                aria-label="Go to previous week"
              >
                <ChevronLeftIcon className="mr-1" /> Previous week
              </Button>
              <Button
                size="sm"
                disabled={week === totalWeeks}
                onClick={() => {
                  setWeek(Math.min(totalWeeks, (week || 1) + 1));
                  setDate(undefined);
                  setBookableDays([]);
                }}
                aria-label="Go to next week"
              >
                Next week <ChevronRightIcon className="ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SlotButton({
  providerId,
  providerName,
  start,
  end,
  isBookingLoading,
  setIsBookingLoading,
}: {
  providerId: string;
  providerName: string;
  start: DateTime;
  end: DateTime;
  isBookingLoading: boolean;
  setIsBookingLoading: (isBookingLoading: boolean) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const { uniqueId, selectedAppointment, appendBookingOutcomes, caseStudyId, planOfCareId, setCrossCaseStudyBlockedDates, crossCaseStudyBlockedDates } = usePlanOfCareStore();
  const { data: detailsData } = usePlanOfCareDetails({ uniqueId, enabled: Boolean(uniqueId) });

  const handleBookAppointment = useCallback(
    async ({
      providerId,
      providerName,
      start,
      end,
      appointmentToBook,
    }: {
      providerId: string;
      providerName: string;
      start: DateTime;
      end: DateTime;
      appointmentToBook: {
        id: string;
        appointmentTypeId: string;
        appointmentTypeName: string;
        appointmentDuration: number;
      };
    }) => {
      setIsLoading(true);
      setIsBookingLoading(true);

      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/schedule`, {
          uniqueId: uniqueId,
          startDate: start.toISO(),
          endDate: end.toISO(),
          providerId: providerId,
          appointmentTypeId: appointmentToBook.appointmentTypeId,
          isPlanOfCare: true,
          caseStudyId: caseStudyId,
          planOfCareId: planOfCareId,
        });

        // Only append outcomes on success
        appendBookingOutcomes({
          status: "Scheduled",
          id: appointmentToBook.id,
          appointmentTypeId: appointmentToBook.appointmentTypeId,
          appointmentTypeName: appointmentToBook.appointmentTypeName,
          appointmentDuration: appointmentToBook.appointmentDuration,
          providerId: providerId,
          providerName: providerName,
          start: start,
          caseStudyId: caseStudyId,
        });

        // Add this date to cross-case study blocked dates so other case studies can't book same day,
        // unless the organization allows same-day appointments across provider groups.
        if (!detailsData?.organization.allowSameDayAppointmentsAcrossProviderGroups) {
          const dateKey = start.toISODate();
          if (dateKey) {
            const updatedBlockedDates = new Set(crossCaseStudyBlockedDates);
            updatedBlockedDates.add(dateKey);
            setCrossCaseStudyBlockedDates(updatedBlockedDates);
          }
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || "There was an error scheduling your appointment. Please try again or contact the clinic for help.");
      } finally {
        setIsLoading(false);
        setIsBookingLoading(false);
      }
    },
    [uniqueId, appendBookingOutcomes, caseStudyId, planOfCareId, crossCaseStudyBlockedDates, setCrossCaseStudyBlockedDates, detailsData?.organization.allowSameDayAppointmentsAcrossProviderGroups],
  );

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-full dark:bg-accent dark:hover:bg-muted"
      disabled={isLoading || isBookingLoading}
      onClick={() => {
        handleBookAppointment({
          providerId: providerId,
          providerName: providerName,
          start: start,
          end: end,
          appointmentToBook: selectedAppointment,
        });
      }}
    >
      {isLoading && <Loader2 className="size-4 animate-spin" />}
      {providerName}
    </Button>
  );
}

function ProviderSelect({ providerOptions }: { providerOptions: { label: string; value: string }[] }) {
  const { selectedProvider, setSelectedProvider, setProviderOptions } = usePlanOfCareStore();

  const allProviderOptions = useMemo(() => {
    let options = [{ label: "All Providers", value: "all" }];
    if (providerOptions && providerOptions.length > 0) {
      options = [...options, ...providerOptions];
    }
    setProviderOptions(options);
    return options;
  }, [providerOptions, setProviderOptions]);

  return (
    <Select
      value={selectedProvider.value}
      onValueChange={(value) =>
        setSelectedProvider(
          allProviderOptions.find((option) => option.value === value) || {
            label: "All Providers",
            value: "all",
          },
        )
      }
    >
      <SelectTrigger aria-label="Select provider" className="w-auto max-w-full min-w-40">
        <SelectValue placeholder="Select provider" />
      </SelectTrigger>
      <SelectContent>
        {allProviderOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DayButton({
  dateISO,
  disabled,
  hasAvailability,
  isLoadingAvailability,
  isBookable,
  isNonWorkingDay,
  debugActive,
  disabledReason,
}: {
  dateISO: string;
  disabled?: boolean;
  hasAvailability?: boolean;
  isLoadingAvailability?: boolean;
  isBookable?: boolean;
  isNonWorkingDay?: boolean;
  debugActive?: boolean;
  disabledReason?: string;
}) {
  const { date, setDate } = usePlanOfCareStore();

  if (!dateISO) {
    return <Skeleton className="w-full aspect-square" />;
  }

  const buttonDate = DateTime.fromISO(dateISO);
  const isSelected = date?.hasSame(buttonDate, "day");

  // Show dots only when loaded, bookable, and the provider actually works that day
  const shouldShowDot = !isLoadingAvailability && isBookable && !isNonWorkingDay;

  // In the clinic-user debug view, crossed-out days stay clickable so their
  // availability/compliance data can be inspected, even though they're not
  // actually bookable.
  const clickDisabled = disabled && !debugActive;
  const isCrossedOut = (disabled && !isBookable) || isNonWorkingDay;

  const button = (
    <Button
      variant="ghost"
      size="icon"
      disabled={clickDisabled}
      onClick={() => setDate(DateTime.fromISO(dateISO))}
      className={cn(
        "w-full aspect-square h-auto bg-accent lg:hover:ring-[1.5px] hover:ring-ring hover:bg-accent ring-0 font-normal",
        // Add dot indicator for availability status
        shouldShowDot && "relative after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-1.5 after:w-1.5 after:h-1.5 after:rounded-full",
        // Green dot for availability (keep green even when selected)
        shouldShowDot && hasAvailability && "after:bg-emerald-500",
        // Red dot for no availability (keep red even when selected)
        shouldShowDot && !hasAvailability && "after:bg-destructive",
        // Selected state
        isSelected && !clickDisabled && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
      )}
    >
      {isCrossedOut ? <span className="line-through">{buttonDate.toFormat("d")}</span> : buttonDate.toFormat("d")}
    </Button>
  );

  if (debugActive && isCrossedOut && disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
